/**
 * DashboardModePicker — iter163.
 *
 * One-time onboarding modal that asks new users to pick their dashboard
 * style. Saves the choice to `simple_dashboard` on `users.me`. Once set,
 * the modal never appears again for that user.
 *
 * Mount it once near the top of HomeRoute / SimpleDashboard so any first
 * login post-signup triggers it. Existing users (any explicit
 * `simple_dashboard: true|false`) skip it entirely.
 */
import { useState } from 'react';
import { Sparkles, Layers, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function DashboardModePicker() {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [choice, setChoice] = useState(null); // 'simple' | 'advanced'

  // Only render when the user has NEVER chosen — i.e. the field is undefined or null.
  const needsPicker = !!user && (user.simple_dashboard === undefined || user.simple_dashboard === null);
  if (!needsPicker) return null;

  const save = async (mode) => {
    if (busy) return;
    setBusy(true);
    setChoice(mode);
    try {
      await api.put('/users/me', { simple_dashboard: mode === 'simple' });
      if (refreshUser) await refreshUser();
      toast.success(`Set to ${mode === 'simple' ? 'Simple' : 'Advanced'} dashboard`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save preference');
      setChoice(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => { /* not dismissable; force a choice */ }}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden"
        data-testid="dashboard-mode-picker"
        // Disable outside-click and ESC closing — user must pick.
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="px-6 pt-6 pb-4">
          <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Welcome to SplitEasy</p>
          <DialogTitle asChild>
            <h2 className="text-2xl font-display font-bold mt-1">Pick your view</h2>
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
            How would you like the app to look? You can switch any time from{' '}
            <span className="font-semibold">Profile → Show simple dashboard</span>.
          </p>
        </div>
        <div className="px-4 pb-4 space-y-3">
          <button
            type="button"
            onClick={() => save('simple')}
            disabled={busy}
            data-testid="dashboard-mode-simple"
            className={`w-full text-left p-4 rounded-xl border-2 transition flex items-start gap-3 ${
              choice === 'simple'
                ? 'border-primary bg-primary/10'
                : 'border-muted hover:border-primary/40 hover:bg-muted/30'
            }`}
          >
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-base flex items-center gap-1.5">
                Simple
                <span className="text-xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 font-bold">
                  Recommended
                </span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                Big balance card · Scan to Pay & Pay by Mobile · Quick links to expenses, funds, sessions. Best on mobile.
              </p>
            </div>
            {choice === 'simple' && <Check className="w-5 h-5 text-primary shrink-0" />}
          </button>
          <button
            type="button"
            onClick={() => save('advanced')}
            disabled={busy}
            data-testid="dashboard-mode-advanced"
            className={`w-full text-left p-4 rounded-xl border-2 transition flex items-start gap-3 ${
              choice === 'advanced'
                ? 'border-primary bg-primary/10'
                : 'border-muted hover:border-primary/40 hover:bg-muted/30'
            }`}
          >
            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0">
              <Layers className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-base">Advanced</p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                Full admin view with reconcile tools, fund tabs, integrity reports, and bulk actions. Best for treasurers and group admins.
              </p>
            </div>
            {choice === 'advanced' && <Check className="w-5 h-5 text-primary shrink-0" />}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
